<template>
  <div class="product-form__options">
    <div
      v-if="productHasColors"
      class="product-form__options-item product-form__colors"
    >
      <p
        id="product-form-color"
        class="h6"
      >
        Color: <span>{{ getColorString(product.swatch.color_name) }}</span>
      </p>
      <ul class="product-form__options-list product-form__options-list--color mb-0">
        <li
          v-for="color in product.color_variants"
          :key="color.id"
          class="product-form__color-item"
        >
          <a
            :href="color.url"
            :class="color.swatch.color_name === product.swatch.color_name ? 'active' : ''"
            :aria-label="getColorString(color.swatch.color_name)"
            aria-labelledby="product-form-color"
          >
            <img
              :src="color.swatch.url"
              :alt="getColorString(color.swatch.color_name)"
            >
          </a>
        </li>
      </ul>
    </div>
    <div
      v-for="(option, optionIndex) in productOptions"
      :key="optionIndex"
      class="product-form__options-item"
    >
      <div class="product-form__options-item-heading">
        <p class="h6">
          {{ option.name }}: {{ selectedOptions[`option_${option.position}`] }}
        </p>
        <slot
          v-if="showSizeGuideLink(option.name)"
          name="sizeChart"
        />
      </div>
      <ul
        v-if="isChipOption(option.name)"
        class="product-form__options-list mb-0"
      >
        <li
          v-for="(value, valueIndex) in option.values"
          :key="valueIndex"
          class="product-form__chip-item"
        >
          <div
            class="product-form__chip-option position-relative"
            :class="getOptionClasses(`option_${option.position}`, value)"
          >
            <label
              class="m-0"
              :for="value"
            >{{ value }}</label>
            <input
              :id="value"
              v-model="selectedOptions[`option_${option.position}`]"
              type="radio"
              :name="`options[${option.name}]`"
              :value="value"
            >
          </div>
        </li>
      </ul>
      <div
        v-else
        class="product-form__options-list"
      >
        <SelectInput
          v-model="selectedOptions[`option_${option.position}`]"
          :options="option.values"
          @input="onSelectChange(option.name, `option_${option.position}`)"
        />
        <select
          :ref="`select-${option.name}`"
          class="d-none"
          :name="`options[${option.name}]`"
        >
          <option
            v-for="(value, valueIndex) in option.values"
            :key="valueIndex"
            :value="value"
          >
            {{ value }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import { getColorName } from 'scripts/helpers/product';
import SelectInput from 'scripts/components/forms/SelectInput.vue';

export default {
  components: {
    SelectInput,
  },

  props: {
    onQuickShop: {
      type: Boolean,
      default: false,
    },

    product: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      chipOptions: ['size', 'shoe size', 'size shoe'],
      variantLoaded: false,
      selectedOptions: {
        'option_1': null,
        'option_2': null,
        'option_3': null,
      },
    };
  },

  computed: {
    currentVariant() {
      let currentVariant = null;

      Object.entries(this.selectedOptions).forEach(([optionKey, value]) => {
        if (this.selectedOptions[optionKey]) {
          currentVariant = this.product.variants.filter((variant) => variant[optionKey] === value);
        }
      });

      return currentVariant && currentVariant.length ? currentVariant[0] : null;
    },

    productHasColors() {
      return this.product.color_variants.length > 0;
    },

    productOptions() {
      return this.product.has_only_default_variant ? [] : this.product.options_with_values;
    },

    sizeChartText() {
      return window.bvaccel.language.size_chart_text;
    },
  },

  watch: {
    currentVariant() {
      const variantSelector = document.querySelector('[data-variant-select]');
      variantSelector.value = this.currentVariant.id;

      if (!this.currentVariant.available) {
        this.updateFormButton(true, window.bvaccel.language.unavailable_product);
      } else {
        this.updateFormButton(false, window.bvaccel.language.available_product);
      }

      if (this.variantLoaded) {
        // eslint-disable-next-line no-undef
        datalayerViewItem(this.product, {
          variant: this.currentVariant,
          event: this.onQuickShop ? 'bva_view_item_quickshop' : 'bva_view_item'
        });
      }

      this.variantLoaded = true;
    },
  },

  mounted() {
    this.initSelectedOptions();
  },

  methods: {
    updateFormButton(disableBtn, textContent) {
      const formButton = document.querySelector('[data-add-to-cart]');
      formButton.textContent = textContent;
      formButton.disabled = disableBtn;

      if (disableBtn) {
        formButton.classList.remove('btn--secondary');
        formButton.classList.add('btn--primary');
      } else {
        formButton.classList.remove('btn--primary');
        formButton.classList.add('btn--secondary');
      }
    },

    getColorString(color) {
      return getColorName(color);
    },

    getOptionClasses(optionKey, optionValue) {
      const variantWithCurrentOption = this.getVariant(optionKey, optionValue);

      return {
        'selected': this.selectedOptions[optionKey] === optionValue,
        'disabled': variantWithCurrentOption ? !variantWithCurrentOption.available : false,
      };
    },

    getSwatchImageAltText(color) {
      return `${this.getColorString(color)} swatch`;
    },

    initSelectedOptions() {
      const currentProduct = this.product.selected_or_first_available_variant;

      Object.keys(this.selectedOptions).forEach((optionKey) => {
        this.selectedOptions[optionKey] = currentProduct[optionKey] ? currentProduct[optionKey] : null;
      });
    },

    isChipOption(optionName) {
      return this.chipOptions.includes(optionName.toLowerCase());
    },

    getVariant(optionKey, optionValue) {
      const currentOptions = {...this.selectedOptions};
      currentOptions[optionKey] = optionValue;
      let variantToReturn = null;

      Object.entries(currentOptions).forEach(([key, value]) => {
        if (this.selectedOptions[key]) {
          variantToReturn = this.product.variants.filter((variant) => variant[key] === value);
        }
      });

      return variantToReturn && variantToReturn.length ? variantToReturn[0] : null;
    },

    onSelectChange(optionName, optionKey) {
      this.$refs[`select-${optionName}`][0].value = this.selectedOptions[optionKey];
    },

    showSizeGuideLink(optionName) {
      return this.chipOptions.includes(optionName.toLowerCase());
    },
  },
}
</script>

<style>

</style>